import React, { JSX } from 'react';
import { Input, InputProps, Label } from 'reactstrap';
import { Controller, FieldValues } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import { useFormContext } from '../Form';
import { BaseFormElement } from '../types/types';
import classNames from 'classnames';
import { sanitize } from '@ibe/services';
import { ApiBaseData } from '@ibe/api';
import { getErrorMessage } from './utils';

interface Props {
  id?: string;
  tag: BaseFormElement['tag'];
  name: string;
  baseName?: string;
  label?: string;
  boldLabel?: boolean;
  className?: string;
  errorsProp?: Record<string, string>;
  showErrors?: boolean;
  showErrorIcon?: boolean;
  values: ApiBaseData[];
  nonNative?: boolean;
}

const FormRadioGroup = (props: Props & Omit<InputProps, 'tag'>): JSX.Element | null => {
  const {
    id,
    tag,
    name,
    baseName,
    label,
    boldLabel,
    errorsProp,
    autoComplete,
    disabled,
    showErrors,
    showErrorIcon,
    values,
    nonNative
  } = props;
  const { register, errors, control, formState } = useFormContext<FieldValues>();
  const { touchedFields, dirtyFields, isSubmitted } = formState || {};
  const elementId = `form-builder-input-${id || name}-${tag}`;

  if (!register) return null;

  const formErrors = errorsProp || errors;
  const fieldName = baseName ?? name;
  const fieldError = formErrors?.[fieldName];
  const errorMessage = getErrorMessage(fieldError);

  return (
    <div
      className={classNames('form-input', `form-input-${name}`, {
        'input--error': formErrors && showErrors && formErrors[baseName ?? name],
        'form-input--errors': !!formErrors?.[baseName ?? name],
        'form-input--touched': touchedFields?.[baseName ?? name],
        'form-input--dirty': dirtyFields?.[baseName ?? name],
        'form-input--form-submitted': isSubmitted
      })}
    >
      {!!label && (
        <Label
          className={`${
            formErrors && showErrors && formErrors[baseName || name] ? 'form__error__top' : ''
          }${boldLabel ? ' font-weight-bold' : ''}`}
          htmlFor={elementId}
          dangerouslySetInnerHTML={{ __html: sanitize(label, true) }}
        />
      )}
      <Controller
        name={name}
        control={control}
        render={({ field }): JSX.Element => (
          <div className="position-relative form-input__radio__group">
            {values.map(value => (
              <Label
                key={value.code}
                className={classNames('form-input__radio__item', { 'mb-3': !nonNative })}
              >
                <div className="form-input__radio__label">{value.description}</div>
                <Input
                  id={`${elementId}-${value.code}`}
                  type="radio"
                  name={field.name}
                  value={value.code}
                  checked={field.value === value.code}
                  onChange={() => field.onChange(value.code)}
                  ref={field.ref}
                  data-testid={`form-input-${field.name}`}
                  className={classNames({ 'input--hidden': nonNative })}
                  autoComplete={autoComplete}
                  disabled={disabled}
                />
                {nonNative && (
                  <div
                    className={classNames('form-input__radio__custom', {
                      'form-input__radio__custom--selected': field.value === value.code
                    })}
                  >
                    <span />
                  </div>
                )}
              </Label>
            ))}
          </div>
        )}
      />
      {showErrors && errorMessage ? <div className="form__error">{errorMessage}</div> : null}
      {showErrorIcon && formErrors && showErrors && formErrors[baseName || name] && (
        <FontAwesomeIcon icon={faExclamationTriangle} />
      )}
    </div>
  );
};

export default FormRadioGroup;
